import React, { useEffect, useMemo, useState } from "react";

const DEFAULT_PAGE_SIZE = 10;
const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

const Table = ({
  columns,
  rows,
  emptyText = "No data available.",
  tableClassName = "",
  className = "",
  compact = false,
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const safeRows = Array.isArray(rows) ? rows : [];
  const totalRows = safeRows.length;
  const totalPages = Math.max(1, Math.ceil(totalRows / pageSize));

  useEffect(() => {
    setCurrentPage(1);
  }, [pageSize]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const paginatedRows = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    const end = start + pageSize;
    return safeRows.slice(start, end);
  }, [safeRows, currentPage, pageSize]);

  const startIndex = (currentPage - 1) * pageSize + 1;
  const endIndex = Math.min(currentPage * pageSize, totalRows);

  if (totalRows === 0) {
    return (
      <div className="p-2 text-sm text-slate-500 dark:text-slate-400">
        {emptyText}
      </div>
    );
  }

  return (
    <div className={["space-y-2", className].filter(Boolean).join(" ")}>
      <div className="overflow-x-auto">
        <table
          className={[
            "w-full min-w-full border-separate border-spacing-0 text-left text-[13px] text-slate-700 dark:text-slate-300",
            tableClassName,
          ]
            .filter(Boolean)
            .join(" ")}
        >
          <thead className="sticky top-0 z-10 bg-slate-50/70 text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-500 dark:bg-slate-800/40 dark:text-slate-400">
            <tr>
              {columns.map((column) => (
                <th
                  key={column.key}
                  className={[
                    compact
                      ? "border-b border-slate-200 px-3 py-2 first:pl-3 last:pr-3 dark:border-slate-700"
                      : "border-b border-slate-200 px-4 py-2.5 first:pl-4 last:pr-4 dark:border-slate-700",
                    column.headerClassName || "",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                >
                  {column.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="text-slate-700 dark:text-slate-300">
            {paginatedRows.map((row, index) => (
              <tr
                key={row.id || row.key || index}
                className={[
                  "transition-colors hover:bg-slate-50/80 dark:hover:bg-slate-800/30",
                  row?.className || "",
                ]
                  .filter(Boolean)
                  .join(" ")}
              >
                {columns.map((column) => (
                  <td
                    key={column.key}
                    className={[
                      compact
                        ? "border-b border-slate-200 px-3 py-2.5 align-middle first:pl-3 last:pr-3 dark:border-slate-700"
                        : "border-b border-slate-200 px-4 py-3 align-middle first:pl-4 last:pr-4 dark:border-slate-700",
                      column.cellClassName || "",
                    ]
                      .filter(Boolean)
                      .join(" ")}
                  >
                    {typeof column.render === "function"
                      ? column.render(row)
                      : row[column.key]}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {totalRows > 0 && (
        <div
          className={[
            "flex flex-col gap-2 py-1 text-slate-500 dark:text-slate-400 sm:flex-row sm:items-center sm:justify-between",
            compact ? "px-0.5 text-[11px] sm:px-1" : "px-1 text-xs sm:px-2",
          ]
            .filter(Boolean)
            .join(" ")}
        >
          <div className="text-center sm:text-left">
            Showing {startIndex}-{endIndex} of {totalRows}
          </div>

          <div className="flex flex-wrap items-center justify-center gap-2 sm:justify-end">
            <label className="inline-flex items-center gap-2">
              <span>Rows</span>
              <select
                value={pageSize}
                onChange={(e) => setPageSize(Number(e.target.value) || DEFAULT_PAGE_SIZE)}
                className={[
                  "rounded-md border border-slate-300 bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-400 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200 dark:focus:ring-slate-500",
                  compact ? "px-2 py-1 text-[11px]" : "px-2 py-1 text-xs",
                ]
                  .filter(Boolean)
                  .join(" ")}
              >
                {PAGE_SIZE_OPTIONS.map((size) => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </select>
            </label>

            <button
              type="button"
              onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
              disabled={currentPage <= 1}
              className={[
                "rounded-md border border-slate-300 font-medium text-slate-600 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-700/60",
                compact ? "px-2.5 py-1 text-[11px]" : "px-2.5 py-1 text-xs",
              ]
                .filter(Boolean)
                .join(" ")}
            >
              Prev
            </button>
            <span
              className={[
                "min-w-[72px] text-center font-medium text-slate-600 dark:text-slate-300",
                compact ? "text-[11px]" : "text-xs",
              ]
                .filter(Boolean)
                .join(" ")}
            >
              {currentPage} / {totalPages}
            </span>
            <button
              type="button"
              onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
              disabled={currentPage >= totalPages}
              className={[
                "rounded-md border border-slate-300 font-medium text-slate-600 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-700/60",
                compact ? "px-2.5 py-1 text-[11px]" : "px-2.5 py-1 text-xs",
              ]
                .filter(Boolean)
                .join(" ")}
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Table;
